import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, Router } from '@angular/router';
import { environment } from '@environments/environment';
import { CacheDB } from '@shared/services/indexed-db';
import { LanguageAvailable, LanguageData } from '@shared/services/language';
import { LogsData } from '@shared/services/logs';
import { findBestMatchingLanguage, getDomainConfig, mapGenericLanguageToRegion } from '@shared/utils';
import { AppStore } from '@stores';
import { map, Observable, of, switchMap, take } from 'rxjs';

/**
 * Default configuration object used as a fallback.
 */
const FALLBACK_CONFIG = {
  brand: 'perene',
  defaultLang: 'fr',
  supported: ['fr'],
} as const;

export const languageResolver: ResolveFn<boolean> = (route: ActivatedRouteSnapshot): Observable<boolean> => {
  const appStore = inject(AppStore);
  const document = inject(DOCUMENT);
  const language = inject(LanguageData);
  const cacheDB = inject(CacheDB);
  const logs = inject(LogsData);
  const router = inject(Router);

  // Optimization: If language is already set, skip initialization
  if (appStore.read()?.currentLanguage) {
    return of(true);
  }

  const windowRef = document.defaultView as Window | null;
  const domainConfig = getDomainConfig(document.location.hostname);

  const configToUse = domainConfig || {
    brand: FALLBACK_CONFIG.brand,
    language: {
      default: FALLBACK_CONFIG.defaultLang,
      supported: [...FALLBACK_CONFIG.supported],
    },
  };

  if (!domainConfig) {
    logs.error(
      'Startup',
      'Failed to load domain configuration data',
      document.location.hostname,
      'Available domains:',
      Object.keys(environment.domains)
    );
  }

  const languageIdParam = route.firstChild?.paramMap.get('languageId') || route.paramMap.get('languageId');
  const urlLanguage = (languageIdParam || null) as LanguageAvailable | null;
  const supportedLanguages = configToUse.language.supported;
  const navigatorLanguages = windowRef?.navigator?.languages ?? [];
  const mappedUrlLang = urlLanguage ? mapGenericLanguageToRegion(urlLanguage, supportedLanguages) : null;
  const isUrlLangValid = mappedUrlLang && supportedLanguages.includes(mappedUrlLang);

  const languageSource$ = isUrlLangValid
    ? of(mappedUrlLang)
    : cacheDB.get<LanguageAvailable>('language').pipe(
        take(1),
        map((cachedLang) => {
          if (cachedLang && supportedLanguages.includes(cachedLang)) {
            return cachedLang;
          }
          return findBestMatchingLanguage(navigatorLanguages, supportedLanguages, configToUse.language.default);
        })
      );

  return languageSource$.pipe(
    switchMap((selectedLanguage) => {
      cacheDB.upsert('language', selectedLanguage).pipe(take(1)).subscribe();

      appStore.batchUpdate({
        update: {
          branding: configToUse.brand,
          currentLanguage: selectedLanguage,
          languagesAvailable: supportedLanguages,
        },
      });

      return language.use(selectedLanguage).pipe(
        map(() => {
          if (urlLanguage) {
            void router.navigate(['/'], { replaceUrl: true });
          }
          return true;
        })
      );
    })
  );
};
